import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Spinner } from 'react-bootstrap';
import { useTheme } from '../../ThemeContext';
import SalesFeed from '../SalesFeed';
import { API_URL } from '../../config/endpoints';
import './PublicLanding.css';

const heroChips = [
  'Чат с карточками товаров',
  'Подбор промокодов',
  'Реальная история цен',
  'Лист отслеживания',
  'Барахолка участников',
  'Пуши по вашим фильтрам',
  'WB • Ozon • Детмир',
];

const featureCards = [
  {
    label: 'Лента',
    title: 'Реальные скидки, а не шум',
    text: 'Salew Bot фильтрует поток товаров по маркетплейсу, категории, остатку, скидке, AI и бонусам за отзыв.',
    points: ['WB, Ozon и Детмир', 'остатки и размеры', 'рубли за отзыв', 'пуши по фильтрам'],
  },
  {
    label: 'История',
    title: 'Понятно, где скидка настоящая',
    text: 'Смотрите реальный диапазон цен по товару и не ведитесь на искусственно поднятую старую цену.',
    points: ['диапазон цены', 'динамика изменений', 'контроль выгодной цены'],
  },
  {
    label: 'Слежение',
    title: 'Лист отслеживания под ваши цели',
    text: 'Добавляйте артикулы, выбирайте размеры и желаемую цену. Когда товар подходит под условия, приложение сообщит.',
    points: ['артикул WB', 'нужные размеры', 'цель по цене', 'уведомление'],
  },
  {
    label: 'Промо',
    title: 'Промокоды без ручного поиска',
    text: 'В чате можно написать запрос вроде “промокод на еду” или “промокод яндекс маркет” и открыть подборку кодов.',
    points: ['поиск по смыслу', 'копирование в один клик', 'коды не засоряют чат'],
  },
  {
    label: 'Чат',
    title: 'Закрытый чат с полезной механикой',
    text: 'Участники делятся находками, отвечают на сообщения, отправляют фото и ссылки, которые превращаются в товарные карточки.',
    points: ['ответы и фото', 'WB/Ozon/Детмир карточки', 'профили участников'],
  },
  {
    label: 'Маркет',
    title: 'Барахолка для удачных находок',
    text: 'Купили лишнее, поймали хороший товар или хотите перепродать улов? Добавьте карточку, фото и способ связи.',
    points: ['свои товары', 'фото и описание', 'связь через профиль'],
  },
];

const chatHighlights = [
  {
    title: 'Ссылка превращается в карточку',
    text: 'WB, Ozon и Детмир отображаются в чате как аккуратная карточка товара с ценой, фото и важными деталями.',
  },
  {
    title: 'Промокоды открываются отдельно',
    text: 'Запрос на промокоды не улетает сообщением в чат. Вместо этого открывается подборщик с кодами.',
  },
  {
    title: 'Профили и товары участников',
    text: 'Можно открыть профиль участника, посмотреть его товары и быстро понять, с кем связаться.',
  },
];

const marketSamples = [
  { title: 'Детский комплект', price: '650 ₽', meta: 'новый, с биркой' },
  { title: 'Косметика WB', price: '290 ₽', meta: 'выгодный улов' },
  { title: 'Наушники', price: '1 200 ₽', meta: 'продажа участника' },
];

const videoCards = [
  { id: 'ios', title: 'Установка на iOS', text: 'Как добавить PWA на главный экран iPhone.' },
  { id: 'android', title: 'Установка на Android', text: 'Как открыть приложение как полноценный сервис.' },
  { id: 'feed', title: 'Лента скидок', text: 'Что означают цены, остатки, размеры и метки.' },
  { id: 'watchlist', title: 'Лист отслеживания', text: 'Как не пропустить нужную цену по товару.' },
];

function useContainerWidth() {
  const ref = React.useRef(null);
  const [w, setW] = React.useState(0);

  React.useEffect(() => {
    if (!ref.current) return undefined;

    const updateWidth = () => setW(ref.current?.getBoundingClientRect().width || 0);
    updateWidth();

    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }

    const ro = new ResizeObserver((entries) => {
      for (const e of entries) setW(e.contentRect.width);
    });
    ro.observe(ref.current);
    return () => ro.disconnect();
  }, []);

  return [ref, w];
}

const LandingButton = ({ children, className = '', ...props }) => (
  <Button variant="light" className={`sb-action ${className}`} {...props}>
    {children}
  </Button>
);

const VideoStripe = () => (
  <section className="sb-section sb-videos" aria-labelledby="sb-videos-title">
    <div className="sb-section-head">
      <div>
        <span className="sb-kicker">Видео</span>
        <h2 id="sb-videos-title" className="sb-section-title">Быстрый старт без путаницы</h2>
      </div>
      <Link to="/videos" className="sb-text-link">Смотреть все</Link>
    </div>

    <div className="sb-video-row" role="list">
      {videoCards.map((video, index) => (
        <Link className="sb-video-card" key={video.id} to="/videos" role="listitem">
          <span className="sb-video-index">{String(index + 1).padStart(2, '0')}</span>
          <h3>{video.title}</h3>
          <p>{video.text}</p>
          <span className="sb-video-more">Открыть инструкцию</span>
        </Link>
      ))}
    </div>
  </section>
);

const YesterdayInline = () => {
  const [salesData, setSalesData] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [boxRef, boxWidth] = useContainerWidth();

  React.useEffect(() => {
    const ctrl = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${API_URL}/get_yesterday_sales?days=1`, { signal: ctrl.signal });
        const data = await res.json();
        if (data.status && data.data_sale) setSalesData(data.data_sale);
        else setError('Не удалось загрузить вчерашние скидки');
      } catch (e) {
        if (e.name !== 'AbortError') setError('Ошибка подключения');
      } finally {
        setLoading(false);
      }
    })();
    return () => ctrl.abort();
  }, []);

  return (
    <section className="sb-section sb-yesterday" aria-labelledby="sb-yesterday-title">
      <div className="sb-section-head">
        <div>
          <span className="sb-kicker">Доказательство</span>
          <h2 id="sb-yesterday-title" className="sb-section-title">Реальные находки за вчера</h2>
        </div>
        <Link to="/yesterday-sales" className="sb-text-link">Все скидки</Link>
      </div>

      <div className="sb-yesterday-feed">
        <div className="sb-yesterday-grid-box" ref={boxRef}>
        {loading ? (
          <div className="sb-loading">
            <Spinner animation="border" size="sm" />
            <span>Загружаем примеры скидок</span>
          </div>
        ) : error ? (
          <div className="sb-glass-alert sb-glass-alert--danger" role="alert">{error}</div>
        ) : (
          <SalesFeed sales={salesData} type="main" containerWidth={boxWidth} />
        )}
        </div>
      </div>
    </section>
  );
};

const FeatureCard = ({ feature }) => (
  <article className="sb-feature-card">
    <div className="sb-feature-label">{feature.label}</div>
    <h3>{feature.title}</h3>
    <p>{feature.text}</p>
    <div className="sb-feature-points">
      {feature.points.map((point) => <span key={point}>{point}</span>)}
    </div>
  </article>
);

const PublicLanding = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const navigate = useNavigate();
  const goLogin = () => navigate('/login');

  return (
    <div className={`sb-root ${isDark ? 'sb-root--dark' : 'sb-root--light'} scrollable`}>
      <div className={`sb-status-glass ${isDark ? 'sb-status-glass--dark' : 'sb-status-glass--light'}`} aria-hidden="true" />

      <header className="sb-header">
        <Link className="sb-logo" to="/" aria-label="Salew Bot">
          <img className="sb-logo-img" src="android-icon-36x36.png" alt="" width={36} height={36} />
          <span>Salew Bot</span>
        </Link>
        <nav className="sb-nav" aria-label="Публичная навигация">
          <Link className="sb-nav-link" to="/yesterday-sales">Вчерашние скидки</Link>
          <Link className="sb-nav-link" to="/videos">Видео</Link>
          <LandingButton className="sb-action--compact" onClick={goLogin}>Авторизация</LandingButton>
        </nav>
      </header>

      <main className="sb-main">
        <section className="sb-hero" aria-labelledby="sb-hero-title">
          <div className="sb-hero-copy">
            <span className="sb-hero-badge">PWA для выгодных покупок</span>
            <h1 id="sb-hero-title">Скидки, промокоды и находки в одном приложении</h1>
            <p>
              Salew Bot ищет реальные скидки, отслеживает цены по вашим параметрам и фильтрам,
              показывает честную историю цены, подбирает промокоды и помогает обмениваться находками
              в закрытом чате. Вы открываете приложение — и сразу видите, где действительно есть выгода.
            </p>
            <div className="sb-hero-actions">
              <LandingButton className="sb-action--primary" size="lg" onClick={goLogin}>Войти и настроить скидки</LandingButton>
              <Link to="/yesterday-sales" className="sb-action-link">Посмотреть реальные находки</Link>
            </div>
          </div>

          <div className="sb-hero-art" aria-label="Фирменный арт Salew Bot">
            <div className="sb-art-orb sb-art-orb--one" />
            <div className="sb-art-orb sb-art-orb--two" />
            <img src="backall.png" alt="Salew Bot и товары" loading="eager" />
            <div className="sb-hero-chips" aria-label="Возможности приложения">
              {heroChips.map((chip) => <span key={chip}>{chip}</span>)}
            </div>
          </div>
        </section>

        <section className="sb-section sb-features" aria-labelledby="sb-features-title">
          <div className="sb-section-head sb-section-head--center">
            <div>
              <span className="sb-kicker">Возможности</span>
              <h2 id="sb-features-title" className="sb-section-title">Что работает внутри Salew Bot</h2>
              <p className="sb-section-subtitle">Не просто список скидок, а набор инструментов, чтобы быстрее понять выгоду и не пропустить нужный товар.</p>
            </div>
          </div>
          <div className="sb-feature-grid">
            {featureCards.map((feature) => <FeatureCard key={feature.title} feature={feature} />)}
          </div>
        </section>

        <section className="sb-section sb-chat-showcase" aria-labelledby="sb-chat-title">
          <div className="sb-chat-copy">
            <span className="sb-kicker">Закрытый чат</span>
            <h2 id="sb-chat-title" className="sb-section-title">Чат, который понимает покупки</h2>
            <p className="sb-section-subtitle">
              Внутри можно обсуждать находки, отвечать на сообщения, отправлять фото и получать полезные карточки вместо голых ссылок.
            </p>
            <LandingButton className="sb-action--primary" onClick={goLogin}>Попасть в чат</LandingButton>
          </div>
          <div className="sb-chat-panel">
            <div className="sb-chat-bubble sb-chat-bubble--left">промокод яндекс маркет</div>
            <div className="sb-chat-sheet">
              <span>Подбор промокодов</span>
              <strong>Коды сгруппированы по маркетам и условиям</strong>
            </div>
            <div className="sb-chat-bubble sb-chat-bubble--right">https://wildberries.ru/catalog/...</div>
            <div className="sb-chat-product-mini">
              <div className="sb-product-photo" />
              <div>
                <span>Карточка товара</span>
                <strong>цена, фото, история и детали</strong>
              </div>
            </div>
          </div>
          <div className="sb-chat-highlights">
            {chatHighlights.map((item) => (
              <article key={item.title}>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="sb-section sb-market-showcase" aria-labelledby="sb-market-title">
          <div>
            <span className="sb-kicker">Барахолка</span>
            <h2 id="sb-market-title" className="sb-section-title">Удачные находки можно перепродать</h2>
            <p className="sb-section-subtitle">
              Если купили лишнее или поймали товар по хорошей цене, добавьте его в барахолку. Участники увидят фото, описание, цену и ваш публичный профиль.
            </p>
            <LandingButton className="sb-action--primary" onClick={goLogin}>Добавить свой товар</LandingButton>
          </div>
          <div className="sb-market-cards" role="list">
            {marketSamples.map((item) => (
              <article className="sb-market-card" key={item.title} role="listitem">
                <div className="sb-market-photo" />
                <div className="sb-market-info">
                  <strong>{item.title}</strong>
                  <span>{item.meta}</span>
                </div>
                <div className="sb-market-price">{item.price}</div>
              </article>
            ))}
          </div>
        </section>

        <VideoStripe />
        <YesterdayInline />

        <section className="sb-section sb-final-cta" aria-labelledby="sb-final-title">
          <span className="sb-kicker">Старт</span>
          <h2 id="sb-final-title">Настройте свои фильтры и ловите только то, что вам реально нужно</h2>
          <p>Авторизуйтесь, выберите категории, остатки, скидки и уведомления. Остальное Salew Bot сделает за вас.</p>
          <div className="sb-final-actions">
            <LandingButton className="sb-action--primary" size="lg" onClick={goLogin}>Авторизация</LandingButton>
            <Link to="/videos" className="sb-action-link">Сначала посмотреть инструкции</Link>
          </div>
        </section>
      </main>

      <footer className="sb-footer">
        <span>© {new Date().getFullYear()} Salew Bot</span>
        <div className="sb-footer-support">
          <a href="https://t.me/Salew_Support" target="_blank" rel="noreferrer">Поддержка в Telegram</a>
          <a href="https://max.ru/u/f9LHodD0cOLkybkMaACuMPTooxWoTrXIaccAneeGAfvHKvf7aKrFOq6HAvA" target="_blank" rel="noreferrer">Поддержка в MAX</a>
        </div>
      </footer>
    </div>
  );
};

export default PublicLanding;
